import { useCallback, useEffect, useState } from "react";

import { ApplicationEndpoints } from "@generated/application";
import { apiFetch, apiPatch } from "@platform/http/apiClient";
import {
  Application,
  ServiceEnvironmentOverride,
  ServiceSpec,
  StackStatus,
} from "src/domains/application/types";

import {
  ENV_KEYS,
  EditableService,
  EnvKey,
  EnvServiceValues,
} from "./editable-services-constants";
import {
  buildMergedServices,
  buildSavePayload,
  buildServiceFromSpec,
  envHasOverrides,
} from "./editable-services-helpers";

// ── Hook: state + data loading + mutation callbacks for EditableServicesSection ─

export interface UseEditableServicesResult {
  services: EditableService[];
  templateServices: ServiceSpec[];
  activeEnv: EnvKey;
  setActiveEnv: (env: EnvKey) => void;
  loading: boolean;
  saving: boolean;
  error: string | null;
  dirty: boolean;
  addingService: boolean;
  setAddingService: (value: boolean) => void;
  isEditable: boolean;
  availableServices: ServiceSpec[];
  envsWithOverrides: Set<EnvKey>;
  handleEnvServiceChange: (
    index: number,
    env: EnvKey,
    updates: Partial<EnvServiceValues>,
  ) => void;
  handleRemoveService: (index: number) => void;
  handleAddService: (serviceSpec: ServiceSpec) => void;
  handleSave: () => Promise<void>;
  handleDiscard: () => void;
}

export function useEditableServices(
  system: Application,
): UseEditableServicesResult {
  const [services, setServices] = useState<EditableService[]>([]);
  const [templateServices, setTemplateServices] = useState<ServiceSpec[]>([]);
  const [activeEnv, setActiveEnv] = useState<EnvKey>("dev");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [addingService, setAddingService] = useState(false);

  const isEditable =
    system.status === StackStatus.DRAFT ||
    system.status === StackStatus.FAILED ||
    system.status === StackStatus.PROVISIONING_FAILED;

  // Compute available services to add
  const availableServices = templateServices.filter(
    (ts) => !services.some((s) => s.name === ts.name),
  );

  // Compute which envs have any overrides across all services (for badge)
  const envsWithOverrides = new Set<EnvKey>();
  for (const svc of services) {
    for (const env of ENV_KEYS) {
      if (
        env !== "dev" &&
        svc.enabledEnvs.has(env) &&
        envHasOverrides(svc, env)
      ) {
        envsWithOverrides.add(env);
      }
    }
  }

  // Load template services and environment overrides on mount
  useEffect(() => {
    let cancelled = false;

    async function loadServices() {
      if (!system.templateSlug) {
        setLoading(false);
        return;
      }

      try {
        const data = await apiFetch<
          { slug: string; services?: ServiceSpec[] }[]
        >(
          `${ApplicationEndpoints.LIST_STACK_TEMPLATES}?systemType=${encodeURIComponent(system.systemType ?? "")}`,
        );

        const allTemplates = Array.isArray(data) ? data : [];
        const template =
          allTemplates.find((t) => t.slug === system.templateSlug) ?? null;

        const templateServicesList = template?.services ?? [];
        const stackConfig = system.stackConfig as
          Record<string, unknown> | undefined;

        // Load saved base services from stackConfig._services
        const savedServices =
          (stackConfig?._services as
            | {
                name: string;
                type: string;
                cpu: number;
                memory: number;
                storage: number;
                replicas: number;
                internetFacing: boolean;
              }[]
            | undefined) ?? [];

        // Load saved environment overrides from stackConfig.environments
        const savedEnvs =
          (stackConfig?.environments as
            | Record<string, { services?: ServiceEnvironmentOverride[] }>
            | undefined) ?? {};

        const merged = buildMergedServices(
          templateServicesList,
          savedServices,
          savedEnvs,
        );

        if (!cancelled) {
          setServices(merged);
          setTemplateServices(templateServicesList);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError("Failed to load service configuration.");
          setLoading(false);
        }
      }
    }

    void loadServices();
    return () => {
      cancelled = true;
    };
  }, [system.templateSlug, system.systemType, system.stackConfig]);

  const handleEnvServiceChange = useCallback(
    (index: number, env: EnvKey, updates: Partial<EnvServiceValues>) => {
      setServices((prev) => {
        const next = [...prev];
        const svc = next[index];
        next[index] = {
          ...svc,
          environments: {
            ...svc.environments,
            [env]: { ...svc.environments[env], ...updates },
          },
        };
        return next;
      });
      setDirty(true);
    },
    [],
  );

  const handleRemoveService = useCallback((index: number) => {
    setServices((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  }, []);

  const handleAddService = useCallback((serviceSpec: ServiceSpec) => {
    setServices((prev) => [...prev, buildServiceFromSpec(serviceSpec)]);
    setDirty(true);
    setAddingService(false);
  }, []);

  /** Build the save payload: services (base) + environments (overrides) */
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { services: servicesPayload, environments } =
        buildSavePayload(services);

      const stackConfig = system.stackConfig as
        Record<string, unknown> | undefined;

      await apiPatch(
        ApplicationEndpoints.UPDATE_APPLICATION.replace(":id", system.alias),
        {
          services: servicesPayload,
          stackConfig: {
            ...(stackConfig ?? {}),
            environments,
          },
        },
      );
      setDirty(false);
    } catch {
      setError("Failed to save service configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = useCallback(() => {
    setDirty(false);
    window.location.reload();
  }, []);

  return {
    services,
    templateServices,
    activeEnv,
    setActiveEnv,
    loading,
    saving,
    error,
    dirty,
    addingService,
    setAddingService,
    isEditable,
    availableServices,
    envsWithOverrides,
    handleEnvServiceChange,
    handleRemoveService,
    handleAddService,
    handleSave,
    handleDiscard,
  };
}
